import React, { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Button,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  Chip,
  Tabs,
  Tab,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { PersonAddOutlined, MusicNote, Headphones } from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';

// Pre-defined genre options
const GENRE_OPTIONS = [
  'House', 'Deep House', 'Tech House', 'Techno', 'Trance', 'Minimal', 'Progressive House',
  'Disco', 'Nu Disco', 'Indie Dance', 'Electronica', 'Ambient', 'Downtempo',
  'Breaks', 'Drum & Bass', 'Dubstep', 'Trap', 'Hip Hop', 'R&B',
  'Pop', 'Rock', 'Indie', 'Alternative', 'Metal', 'Punk',
  'Jazz', 'Blues', 'Soul', 'Funk', 'Reggae', 'Dancehall',
  'Afrobeat', 'Latin', 'Classical'
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    accountType: 'artist', // Default to artist
    artistName: '',
    genre: '',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [genres, setGenres] = useState([]);
  const [localError, setLocalError] = useState(null);
  const { register, loading, resendVerificationEmail } = useContext(AuthContext);
  
  const { 
    username, 
    email, 
    password, 
    confirmPassword, 
    accountType,
    artistName, 
    genre
  } = formData;

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear errors when user starts typing again
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    
    if (localError) {
      setLocalError(null);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Common validations
    if (!username) {
      errors.username = 'Username is required';
    }
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Artist name is always required
    if (!artistName) {
      errors.artistName = 'Artist name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Create registration data with required fields
        const registrationData = {
          username,
          email,
          password,
          accountType,
          artistName // Always include artistName as it's mandatory
        };
        
        if (accountType === 'artist') {
          registrationData.genre = genres.length > 0 ? genres : undefined;
        } else if (accountType === 'curator') {
          registrationData.supportedGenres = genres.length > 0 ? genres : undefined;
        }
        
        const result = await register(registrationData);
        setIsRegistered(true);
        setRegistrationMessage(result.message);
        setUserEmail(email);
        // Don't navigate to dashboard - user needs to verify email first
      } catch (err) {
        setLocalError(err.response?.data?.message || 'Registration failed. Please try again.');
      }
    }
  };

  const handleAddGenre = () => {
    if (genre && !genres.includes(genre)) {
      setGenres([...genres, genre]);
      setFormData({
        ...formData,
        genre: ''
      });
    }
  };

  const handleSelectGenre = (e) => {
    const selectedGenre = e.target.value;
    if (!genres.includes(selectedGenre)) {
      setGenres([...genres, selectedGenre]);
    }
  };

  const handleRemoveGenre = (genreToRemove) => {
    setGenres(genres.filter(g => g !== genreToRemove));
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail(userEmail);
      setRegistrationMessage('Verification email sent! Please check your inbox.');
    } catch (err) {
      setLocalError(err.response?.data?.message || 'Failed to resend verification email.');
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100%', minHeight: '80vh' }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(https://source.unsplash.com/random?music,dj)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: t => t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 4,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <PersonAddOutlined />
          </Avatar>
          <Typography component="h1" variant="h5">
            Join GrooveGate
          </Typography>

          {localError && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {localError}
            </Alert>
          )}

          <Box sx={{ width: '100%', my: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={accountType}
                onChange={(_, newValue) => setFormData({...formData, accountType: newValue})}
                aria-label="account type tabs"
                centered
              >
                <Tab 
                  icon={<MusicNote />} 
                  label="Artist Account" 
                  value="artist" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<Headphones />} 
                  label="Curator Account" 
                  value="curator" 
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph align="center">
              {accountType === 'artist' 
                ? "For artists, producers and labels who want to share their music and get feedback." 
                : "For DJs, bloggers and music professionals who want to discover new music."}
            </Typography>
          </Box>

          {isRegistered ? (
            <Box sx={{ width: '100%', textAlign: 'center', mt: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {registrationMessage}
              </Alert>
              <Typography variant="body2" color="text.secondary" paragraph>
                We've sent a verification link to <strong>{userEmail}</strong>. 
                Please check your email and click the link to verify your account.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Didn't receive the email? Check your spam folder or click below to resend.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleResendVerification}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={20} /> : 'Resend Verification Email'}
              </Button>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Already verified? <Link component={RouterLink} to="/login">Sign in here</Link>
              </Typography>
            </Box>
          ) : (
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <Grid container spacing={2}>
                {/* Common fields for both account types */}
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={handleChange}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={handleChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={handleChange}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                />
              </Grid>

              {/* Divider */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {accountType === 'artist' ? 'Artist Details' : 'Curator Details'}
                  </Typography>
                </Divider>
              </Grid>

              {/* Artist name - required for all account types */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="artistName"
                  label="Artist Name"
                  id="artistName"
                  value={artistName}
                  onChange={handleChange}
                  error={!!formErrors.artistName}
                  helperText={formErrors.artistName}
                />
              </Grid>

              {/* Account type specific fields */}
              {accountType === 'artist' ? (
                <>
                  {/* Additional artist fields can go here */}
                </>
              ) : (
                <>
                  {/* Additional curator fields can go here if needed */}
                </>
              )}

              {/* Genre selection for both types */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="genre-select-label">Select Genre</InputLabel>
                  <Select
                    labelId="genre-select-label"
                    id="genre-select"
                    value=""
                    label="Select Genre"
                    onChange={handleSelectGenre}
                  >
                    {GENRE_OPTIONS.map((genre) => (
                      <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="genre"
                  label="Or Enter Custom Genre"
                  id="genre"
                  value={genre}
                  onChange={handleChange}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddGenre();
                    }
                  }}
                  helperText="Press Enter to add multiple genres"
                  InputProps={{
                    endAdornment: (
                      <Button
                        variant="text"
                        onClick={handleAddGenre}
                        disabled={!genre.trim()}
                      >
                        Add
                      </Button>
                    ),
                  }}
                />
              </Grid>
              
              {genres.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {genres.map((g) => (
                      <Chip
                        key={g}
                        label={g}
                        onDelete={() => handleRemoveGenre(g)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign Up'}
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default RegisterPage;